import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fromWithCompany, supabase } from "../config/supabaseClient";
import { startLoading, stopLoading } from "./commonSlice";

// Async thunk for adding training category
export const addTrainingCatalogue = createAsyncThunk(
  "trainingCatalogue/addTrainingCatalogue",
  async (categoryData, { rejectWithValue }) => {
    try {

      // check training catalogue name is already exists
      let existingCategoryQuery = supabase
        .from("training_catalogue")
        .select("*")
        .eq("name", categoryData.name);

      // Handle null, undefined, empty string, or falsy company_id
      if (!categoryData.company_id || categoryData.company_id === null || categoryData.company_id === undefined || categoryData.company_id === "") {
        existingCategoryQuery = existingCategoryQuery.is("company_id", null);
      } else {
        existingCategoryQuery = existingCategoryQuery.eq("company_id", categoryData.company_id);
      }

      const { data: existingCategory, error: existingCategoryError } = await existingCategoryQuery.single();

      if (existingCategoryError && existingCategoryError.code !== "PGRST116") {
        return rejectWithValue({
          code: existingCategoryError.code,
          details: existingCategoryError.details,
          message: existingCategoryError.message,
        });
      }

      if (existingCategory) {
        return rejectWithValue({
          code: "400",
          details: "Training catalogue name already exists",
          message: "Training catalogue name already exists",
        });
      }

      const { data, error } = await supabase
        .from("training_catalogue")
        .insert(categoryData)
        .select("*")
        .single();

      if (error) {
        console.error("Error adding category:", error);
        return rejectWithValue({
          code: error.code,
          details: error.details,
          message: error.message,
        });
      }

      return data;
    } catch (error) {
      console.error("Error in addTrainingCategory:", error);
      return rejectWithValue({
        code: error.code,
        details: error.details,
        error: error.message,
      });
    }
  }
);

// Async thunk for fetching training categories
export const fetchTrainingCatalogues = createAsyncThunk(
  "trainingCatalogue/fetchTrainingCatalogues",
  async (companyId, { rejectWithValue }) => {
    try {

      let query = supabase
        .from("training_catalogue")
        .select("*")
        .order("created_at", { ascending: false });

      // Handle null company_id for IFundi Catalogue
      if (companyId === null) {
        query = query.is("company_id", null);
      } else {
        query = query.eq("company_id", companyId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }


      return data || [];
    } catch (error) {
      console.error("Error in fetchTrainingCategories:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating category status
export const updateTrainingCatalogueStatus = createAsyncThunk(
  "trainingCatalogue/updateCategoryStatus",
  async ({ categoryId, status }, { rejectWithValue }) => {
    try {

      const { data, error } = await supabase
        .from("training_catalogue")
        .update({ status })
        .eq("id", categoryId)
        .select()
        .single();

      if (error) {
        console.error("Error updating status:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error in updateCategoryStatus:", error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  categories: [],
  loading: false,
  error: null,
  currentCompanyCategory: 0,
  currentIFundiCategory: 0,
};

const trainingCatalogueSlice = createSlice({
  name: "trainingCatalogue",
  initialState,
  reducers: {
    setCategories: (state, action) => {
      state.categories = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentCompanyCategory: (state, action) => {
      state.currentCompanyCategory = action.payload;
    },
    setCurrentIFundiCategory: (state, action) => {
      state.currentIFundiCategory = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addTrainingCatalogue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTrainingCatalogue.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = [action.payload, ...state.categories];
        state.error = null;
      })
      .addCase(addTrainingCatalogue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTrainingCatalogues.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainingCatalogues.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
        state.error = null;
      })
      .addCase(fetchTrainingCatalogues.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTrainingCatalogueStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrainingCatalogueStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex(
          (cat) => cat.id === action.payload.id
        );
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateTrainingCatalogueStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCategories,
  clearError,
  setCurrentCompanyCategory,
  setCurrentIFundiCategory,
} = trainingCatalogueSlice.actions;
export default trainingCatalogueSlice.reducer;
